import React, { useState, useRef, useEffect, useMemo } from "react"
import { apiService } from "../../api/axiosInstance"
import _ from "lodash"

import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"

// hooks
import useSubscribeAllDocs from "../../hooks/useSubscribeAllDocs"

import { DELETE } from "../../config/constants"

// PR
import { Button } from "primereact/button"
import { SplitButton } from "primereact/splitbutton"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Toast } from "primereact/toast"

import Breadcrumb from "../../components/Breadcrumb"

export default function OrganizationList() {
  const [deleting, setDeleting] = useState(false)
  const [pages, setPages] = useState([])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)

  const hookParams = {
    collection: "organizations",
    limit: 15,
    startAfterDoc: pages[currentPageIndex - 1] || null,
  }

  const [loading, error, data, lastVisible] = useSubscribeAllDocs(hookParams)

  useEffect(() => {
    if (lastVisible && pages[currentPageIndex] !== lastVisible) {
      const newPages = [...pages]
      newPages[currentPageIndex] = lastVisible
      setPages(newPages)
    }
  }, [lastVisible])

  const navigate = useNavigate()
  const toast = useRef(null)

  const handleDelete = async (organization_alias) => {
    setDeleting(true)
    try {
      const response = await apiService.post(`/${DELETE.ORGANIZATION}`, { organization_alias })
      if (response.data.status === "success") {
        toast.current.show({
          severity: "success",
          summary: "Organization Deleted",
          detail: "The organization was successfully deleted.",
          life: 3000,
        })
        setDeleting(false)
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error Deleting Organization",
        detail: "An error occurred while deleting the organization.",
        life: 3000,
      })
      setDeleting(false)
    }
  }

  const handleData = useMemo(() => {
    if (!data) return []
    return _.map(data, (row) => {
      const handleActions = (row) => {
        const { id, organization_alias } = row
        const items = [
          {
            label: "Delete",
            icon: "pi pi-times",
            command: () => handleDelete(organization_alias),
          },
        ]
        return (
          <SplitButton
            disabled={deleting}
            label="Edit"
            onClick={() => navigate(`/organization/edit/${id}`)}
            model={items}
          />
        )
      }

      const handleStatus = () => {
        const { organization_status: status } = row
        switch (status) {
          case "ACTIVE":
            return (
              <div style={{ color: "var(--green-600)" }}>
                <i className="pi pi-circle-fill" style={{ marginRight: 10 }} />
                Active
              </div>
            )
          case "INACTIVE":
            return (
              <div style={{ color: "var(--red-400)" }}>
                <i className="pi pi-circle" style={{ marginRight: 10 }} />
                Inactive
              </div>
            )
        }
      }

      const modDate = row.timestamp_updated
        ? row.timestamp_updated.toDate()
        : null

      return {
        ...row,
        updated: DateTime.fromJSDate(modDate).toFormat("F"),
        organization_status: handleStatus(),
        actions: handleActions(row),
      }
    })
  }, [data, deleting])

  if (error) {
    return (
      <div>
        <h2>Error when fetching.</h2>
      </div>
    )
  }

  return (
    <div className="container">
      <h1>Organizations</h1>
      <Breadcrumb items={[{ label: "Manage Organizations" }]} />

      <Button
        onClick={() => navigate("/organization/add")}
        style={{ marginBottom: 15 }}
      >
        Add Organization
      </Button>

      <DataTable value={handleData} responsiveLayout="scroll" loading={loading}>
        <Column
          field="organization_name"
          header="Organization Name"
          filter
          sortable
        />
        <Column field="organization_status" header="Status" sortable />
        <Column field="organization_alias" header="Alias" sortable />
        <Column field="updated" header="Last Update" />
        <Column field="actions" header="Actions" />
      </DataTable>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginTop: 16,
        }}
      >
        <Button
          label="Previous"
          icon="pi pi-angle-left"
          onClick={() => setCurrentPageIndex((i) => Math.max(i - 1, 0))}
          disabled={currentPageIndex === 0}
        />
        <Button
          label="Next"
          icon="pi pi-angle-right"
          iconPos="right"
          onClick={() => setCurrentPageIndex((i) => i + 1)}
          disabled={!lastVisible || !data || data.length < hookParams.limit}
        />
      </div>

      <Toast ref={toast} />
    </div>
  )
}
